import Close from "@mui/icons-material/Close";
import Menu from "@mui/icons-material/Menu";
import MenuOpen from "@mui/icons-material/MenuOpen";
import { Box, IconButton, Stack } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import { SxProps, useTheme } from "@mui/material/styles";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useFrontChatPresent } from "hooks/useFrontChatPresent";
import { useTenantId } from "hooks/useTenant";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";
import { hasScoutAccess } from "routes/Customer/Scout/scoutAccess";

import EncampLogo from "../../assets/EncampLogo.svg";
import LogoWhiteOrange from "../../assets/Logo White Orange.png";
import { NavDrawerScrollSurface } from "./NavDrawerScrollSurface";
import {
  type GroupChildNavItem,
  type GroupNavItem,
  NavItemV2,
  type NavVariant,
  type StandardNavItem,
} from "./NavItemV2";
import {
  NAV_TOGGLE_BUTTON_SIZE,
  NAV_TOGGLE_LEFT,
  TENT_LOGO_LEFT,
  TENT_LOGO_WIDTH,
  WORDMARK_LOGO_LEFT,
  WORDMARK_LOGO_WIDTH,
} from "./navMetrics";
import { SectionNavRow } from "./SectionNavRow";

export type NavSection = {
  title: string;
  longTitle?: string;
  textColor?: string;
  sx?: SxProps;
  /** Controls the heading-divider color; "staff" keeps the pumpkin accent. Defaults to "customer". */
  variant?: NavVariant;
  items: NavItem[];
};

export type NavItem = StandardNavItem | GroupChildNavItem | GroupNavItem;

interface NavDrawerProps {
  navSections: NavSection[];
  collapsed: boolean;
  expandedWidth: string;
  collapsedWidth: string;
  open: boolean;
  /** True when the nav should behave like a mobile overlay drawer instead of a desktop sidebar. */
  isMobileDrawerMode: boolean;
  isMobile: boolean;
  onClose: () => void;
  onNavToggle: () => void;
}

// Use stable keys so each nav row keeps the correct persisted expansion state.
function getNavItemKey(navItem: NavItem, idx: number) {
  switch (navItem.kind) {
    case "Group":
      return `group-${navItem.text}-${navItem.path}`;
    case "Standard":
    case "GroupChild":
      return `${navItem.kind}-${navItem.text}-${navItem.path ?? idx}`;
  }
}

function getSectionKey(section: NavSection, idx: number) {
  return `section-${section.title}-${idx}`;
}

export function LeftNavDrawerV2({
  navSections,
  collapsed,
  expandedWidth,
  collapsedWidth,
  open,
  isMobileDrawerMode,
  isMobile,
  onClose,
  onNavToggle,
}: NavDrawerProps) {
  const theme = useTheme();
  // Reserve space at the bottom of the scroll surface when either Scout's
  // launcher or Front's chat bubble would otherwise overlap the last nav
  // items. Both launchers sit at ~bottom: 20 with a 56px button + breathing
  // room, so ~96px clears them.
  const { featureFlags } = useFeatureFlags();
  const { hasPermissions } = useAuthorization();
  const { isStaff, isEmulating } = useCurrentUser();
  const scoutEnabled = hasScoutAccess({
    flags: featureFlags,
    hasPermissions,
    isStaff: isStaff && !isEmulating,
  });
  const frontPresent = useFrontChatPresent();
  const navBottomPadding = scoutEnabled || frontPresent ? 12 : 0;
  const isCollapsed = isMobileDrawerMode ? false : collapsed;
  const drawerWidth = isMobileDrawerMode
    ? isMobile
      ? "100vw"
      : expandedWidth
    : isCollapsed
      ? collapsedWidth
      : expandedWidth;
  const navToggleLabel = isCollapsed ? "Expand navigation" : "Collapse navigation";
  const drawerVariant = isMobileDrawerMode ? "temporary" : "permanent";
  const closeOnNavigate = isMobileDrawerMode ? onClose : undefined;
  return (
    <Drawer
      variant={drawerVariant}
      open={isMobileDrawerMode ? open : true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          overflowX: "hidden",
          boxSizing: "border-box",
          borderRight: "none",
          transition: (theme) =>
            theme.transitions.create(["width"], {
              easing: isCollapsed
                ? theme.transitions.easing.sharp
                : theme.transitions.easing.easeOut,
              duration: isCollapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <NavDrawerScrollSurface
        data-testid="left-nav-drawer"
        data-nav-mode={drawerVariant}
        data-drawer-width={drawerWidth}
        sx={{
          width: drawerWidth,
        }}
      >
        <Box component="nav" aria-label="Primary navigation" sx={{ minHeight: "100%" }}>
          {isMobileDrawerMode && isMobile && (
            <IconButton
              aria-label="Close navigation"
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                color: "white",
              }}
            >
              <Close />
            </IconButton>
          )}
          {/*
            Header geometry is identical in both nav states (constant paddings,
            sizes, and left-anchored positions), so toggling the drawer never
            shifts the logo or the toggle button — the width change is the only
            animation.
          */}
          <Stack sx={{ paddingTop: theme.spacing(3) }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: isMobileDrawerMode && isMobile ? "flex-start" : undefined,
                paddingLeft: isMobileDrawerMode && isMobile ? theme.spacing(7.5) : undefined,
                marginBottom: isMobileDrawerMode && isMobile ? theme.spacing(3) : undefined,
                alignItems: "center",
                // 24px under the logo, matching the Figma header.
                paddingBottom: theme.spacing(3),
              }}
            >
              <NavLinkToRoot>
                {/*
                  Crossfade the compact tent mark and the white-text wordmark
                  rather than swapping elements. Both stay mounted (already
                  decoded) and are pinned to fixed left offsets — the tent
                  centered in the collapsed icon rail, the wordmark inset from
                  the drawer edge — so neither slides while the drawer width
                  animates; only opacity transitions. The wordmark width is
                  chosen so the orange tent in it renders at roughly the same
                  size as the standalone collapsed tent.
                */}
                <Box sx={{ position: "relative", height: `${TENT_LOGO_WIDTH}px`, width: "100%" }}>
                  <Box
                    component="img"
                    src={EncampLogo}
                    alt="Encamp logo"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: `${TENT_LOGO_LEFT}px`,
                      transform: "translateY(-50%)",
                      width: `${TENT_LOGO_WIDTH}px`,
                      opacity: isCollapsed ? 1 : 0,
                      transition: "opacity 0.15s ease-in-out",
                    }}
                  />
                  <Box
                    component="img"
                    src={LogoWhiteOrange}
                    alt=""
                    aria-hidden
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: `${WORDMARK_LOGO_LEFT}px`,
                      transform: "translateY(-50%)",
                      width: `${WORDMARK_LOGO_WIDTH}px`,
                      opacity: isCollapsed ? 0 : 1,
                      transition: "opacity 0.15s ease-in-out",
                    }}
                  />
                </Box>
              </NavLinkToRoot>
            </Box>
            {!isMobileDrawerMode && (
              <IconButton
                aria-label={navToggleLabel}
                onClick={onNavToggle}
                sx={{
                  color: "white",
                  width: NAV_TOGGLE_BUTTON_SIZE,
                  height: NAV_TOGGLE_BUTTON_SIZE,
                  // Pinned on the icon rail's center line, so it lines up with
                  // every row icon (left-aligned when expanded) and doesn't
                  // shift when the drawer collapses.
                  marginLeft: `${NAV_TOGGLE_LEFT}px`,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                  },
                }}
              >
                {isCollapsed ? (
                  <Menu sx={{ fontSize: "1.5rem" }} />
                ) : (
                  <MenuOpen sx={{ fontSize: "1.5rem" }} />
                )}
              </IconButton>
            )}
          </Stack>
          <List
            // Reserve room so the last nav items can scroll above the FrontChat or
            // Scout floating launcher. Conditional so staff routes (no launchers)
            // keep their original tight padding.
            sx={{ paddingTop: 0, paddingBottom: navBottomPadding }}
          >
            {navSections.map((section, idx) => (
              <Fragment key={getSectionKey(section, idx)}>
                <SectionNavRow section={section} collapsed={isCollapsed} />
                {section.items.map((item, idx) => (
                  <NavItemV2
                    key={getNavItemKey(item, idx)}
                    navItem={item}
                    collapsed={isCollapsed}
                    onNavigate={closeOnNavigate}
                  />
                ))}
              </Fragment>
            ))}
          </List>
        </Box>
      </NavDrawerScrollSurface>
    </Drawer>
  );
}

const NavLinkToRoot = ({ children }: { children: React.ReactNode }) => {
  const { isStaff } = useCurrentUser();
  const tenantId = useTenantId();

  if (tenantId) {
    return <NavLink to={`/o/${tenantId}`}>{children}</NavLink>;
  }

  if (isStaff) {
    return <NavLink to="/staff">{children}</NavLink>;
  }

  return <NavLink to="/">{children}</NavLink>;
};
